/**
 * Revision state (REDLINE_SPEC step 4): save = full snapshot + the
 * authoritative re-redline on the server; the list is server truth.
 * Snapshot corpora are plain projects -- viewing one is just pointing
 * the elements query at snapshot_project_id. The revision_saved stream
 * event invalidates the list for every connected viewer.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE, authFetch } from '../../api';

export interface Revision {
  id: number;
  project_id: string;
  number: number;
  snapshot_project_id: string;
  element_count: number;
  open_findings: number;
  label: string;
}

export function useRevisions(projectId: string) {
  return useQuery<Revision[]>({
    queryKey: ['revisions', projectId],
    queryFn: async () => {
      const res = await authFetch(
        `${API_BASE}/v1/revisions?project_id=${encodeURIComponent(projectId)}`,
      );
      if (!res.ok) throw new Error(`revisions: ${res.status}`);
      return (await res.json()) ?? [];
    },
  });
}

export function useSaveRevision(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await authFetch(`${API_BASE}/v1/revisions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId }),
      });
      if (!res.ok) throw new Error(`save revision: ${res.status}`);
      return res.json() as Promise<Revision>;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['revisions', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['findings', projectId] });
    },
  });
}
